// src/components/Atelier 2/Activite3/DreadedEventsLayer.js
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Shield, Check, Search, ChevronDown, Filter } from 'lucide-react';

const DreadedEventsLayer = ({
  isOpen,
  onClose,
  sourceRisk,
  dreadedEvents,
  mappedDreadedEventIds,
  handleToggleMapping,
  getPillarBadgeColor
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPillar, setFilterPillar] = useState('');

  // Get all unique pillars
  const pillars = [...new Set(dreadedEvents.map(event => event.securityPillar))];

  // Filter dreaded events
  const filteredEvents = dreadedEvents.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPillar = filterPillar ? event.securityPillar === filterPillar : true;

    return matchesSearch && matchesPillar;
  });

  // Group events by pillar
  const groupedEvents = filteredEvents.reduce((acc, event) => {
    const pillar = event.securityPillar || 'Autre';
    if (!acc[pillar]) acc[pillar] = [];
    acc[pillar].push(event);
    return acc;
  }, {});

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg mr-3">
                  <Shield className="text-white" size={24} />
                </div>
                <h2 className="font-bold text-xl text-white">Événements Redoutés</h2>
              </div>
              <div className="ml-11">
                <p className="text-sm text-blue-100 font-medium">
                  Source de risque : <span className="text-white">{sourceRisk.name}</span>
                </p>
                <p className="text-xs text-blue-200 mt-0.5">
                  Catégorie : {sourceRisk.category?.charAt(0).toUpperCase() + sourceRisk.category?.slice(1)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white hover:bg-opacity-20 transition-all duration-200 text-white"
              title="Fermer"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search and filter */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Rechercher
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  placeholder="Rechercher un événement redouté..."
                />
                <Search size={18} className="absolute left-3 top-3 text-gray-400" />
                {searchTerm && (
                  <button
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setSearchTerm('')}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="md:w-64">
              <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                Filtrer par pilier
              </label>
              <div className="relative">
                <select
                  value={filterPillar}
                  onChange={(e) => setFilterPillar(e.target.value)}
                  className="w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                >
                  <option value="">Tous les piliers</option>
                  {pillars.map((pillar) => (
                    <option key={pillar} value={pillar}>
                      {pillar.charAt(0).toUpperCase() + pillar.slice(1)}
                    </option>
                  ))}
                </select>
                <div className="absolute right-0 top-0 h-full flex items-center justify-center w-10 pointer-events-none text-gray-500">
                  <ChevronDown size={16} />
                </div>
              </div>
            </div>
          </div>

          {/* Selection summary */}
          <div className="mt-4 flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold mr-3">
                {mappedDreadedEventIds.length} / {dreadedEvents.length}
              </div>
              <span className="text-sm text-gray-700 font-medium">
                événement(s) sélectionné(s)
              </span>
            </div>
            {(searchTerm || filterPillar) && (
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center font-medium transition-colors"
                onClick={() => {
                  setSearchTerm('');
                  setFilterPillar('');
                }}
              >
                <Filter size={14} className="mr-1.5" />
                Réinitialiser les filtres
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {Object.keys(groupedEvents).length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Search size={32} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Aucun résultat trouvé</h3>
              <p className="text-gray-500 text-sm">Essayez de modifier vos critères de recherche</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(groupedEvents).map(([pillar, events]) => (
                <div key={pillar} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className={`px-4 py-3 ${getPillarBadgeColor(pillar)} font-semibold flex items-center justify-between`}>
                    <span>{pillar.charAt(0).toUpperCase() + pillar.slice(1)}</span>
                    <span className="text-xs bg-white bg-opacity-30 px-2.5 py-1 rounded-full font-bold">
                      {events.length}
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {events.map(event => (
                      <div
                        key={event.id}
                        className={`p-4 hover:bg-gray-50 transition-colors ${mappedDreadedEventIds.includes(event.id) ? 'bg-blue-50' : ''}`}
                      >
                        <div className="flex items-start">
                          <div
                            className={`mt-0.5 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center mr-3 cursor-pointer transition-all ${mappedDreadedEventIds.includes(event.id)
                              ? 'bg-blue-600 border-blue-600 scale-110'
                              : 'border-gray-300 hover:border-blue-400'
                              }`}
                            onClick={() => handleToggleMapping(sourceRisk.id, event.id)}
                          >
                            {mappedDreadedEventIds.includes(event.id) && (
                              <Check size={14} className="text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold text-gray-800 text-sm">{event.name}</h3>
                            </div>
                            {event.description && (
                              <p className="text-sm text-gray-600 mt-1.5 line-clamp-2">{event.description}</p>
                            )}
                            {event.severity && (
                              <div className="mt-2.5">
                                <span className="text-xs text-gray-500 mr-2">Gravité:</span>
                                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${event.severity === 'minor' ? 'bg-green-100 text-green-800' :
                                  event.severity === 'moderate' ? 'bg-yellow-100 text-yellow-800' :
                                    event.severity === 'major' ? 'bg-orange-100 text-orange-800' :
                                      event.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                        event.severity === 'catastrophic' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'
                                  }`}>
                                  {event.severity === 'minor' ? 'Mineure' :
                                    event.severity === 'moderate' ? 'Modérée' :
                                      event.severity === 'major' ? 'Majeure' :
                                        event.severity === 'critical' ? 'Critique' :
                                          event.severity === 'catastrophic' ? 'Catastrophique' :
                                            event.severity}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <div className="bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-semibold">
              {mappedDreadedEventIds.length}
            </div>
            <span className="ml-2 text-sm text-gray-600 font-medium">
              événement(s) sélectionné(s)
            </span>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DreadedEventsLayer;
